// src/webapp.js - Standalone web application entry point
import { render } from 'preact';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router';
import './styles/index.css';
import { ConfigProvider } from './contexts/ConfigContext';
import { DragDropProvider } from './contexts/DragDropContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Client from './api/client';
import { initializeForcedMobileMode } from './utils/mobileMode';
import {
  LazyNavbar,
  LazyGroup,
  LazyDefaultGroup,
  LazyGroupTabs,
  LazyAuthPage,
  LazyTerms,
  LazyPrivacyPolicy,
} from './components/LazyComponents';

// Force the mobile experience when served from mobile.xlibris.app
initializeForcedMobileMode();

// Initialize the API client
const apiClient = new Client();

const container = document.getElementById('root');
if (!container) {
  throw new Error("Couldn't find the root element with id 'root'");
}

// Protected route component - allows anonymous users
const ProtectedRoute = ({ children }) => {
  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

// App wrapper component
const AppWrapper = () => {
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    initializeApp();
  }, []);

  const initializeApp = async () => {
    try {
      // Create anonymous user if needed
      await apiClient.createAnonymousUser();
    } catch (error) {
      console.error('Error initializing app:', error);
    } finally {
      setInitializing(false);
    }
  };

  if (initializing) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading Exlibris...</p>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Navigate to="/groups/default" />} />
        <Route path="/auth" element={<LazyAuthPage />} />
        <Route path="/login" element={<Navigate to="/auth" />} />
        <Route path="/register" element={<Navigate to="/auth" />} />
        <Route path="/terms" element={<LazyTerms />} />
        <Route path="/privacy" element={<LazyPrivacyPolicy />} />
        <Route path="/groups" element={<Navigate to="/groups/default" />} />
        <Route
          path="/group/:id"
          element={
            <ProtectedRoute>
              <DragDropProvider>
                <LazyGroupTabs />
                <LazyGroup />
              </DragDropProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/smart-collections/:smartId"
          element={
            <ProtectedRoute>
              <DragDropProvider>
                <LazyGroupTabs />
                <LazyGroup />
              </DragDropProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/groups/default"
          element={
            <ProtectedRoute>
              <DragDropProvider>
                <LazyNavbar showAddButton={false} />
                <LazyGroupTabs />
                <LazyDefaultGroup />
              </DragDropProvider>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
};

// Render the app
render(
  <ThemeProvider>
    <ConfigProvider>
      <AppWrapper />
    </ConfigProvider>
  </ThemeProvider>,
  container
);

// Ensure search input gets focus after rendering
const focusSearchInput = () => {
  const searchInput = document.querySelector('input[placeholder*="Search"]');
  if (searchInput && document.activeElement !== searchInput) {
    searchInput.focus();
  }
};

// Use requestAnimationFrame for better performance
requestAnimationFrame(() => {
  setTimeout(focusSearchInput, 100);
});
